import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react"
import { Link } from "react-router-dom"
import "../ds/ds.css"
import { prefersReducedMotion } from "../landing/motion"
import { PRIVACY_URL } from "../privacy"
import { SiteLayout, StartButtons } from "./SiteLayout"

/**
 * How we work, v2 (handoff 5 part 2; motion from design/nira/boards/how-we-work-motion.html): the six
 * rules as an index, one answer dissected with the rules marked on it, each rule to try, who we are.
 * The dissected answer is an example built from the product's parts; its case names are made up.
 */

/** The beam: a line that leans with the weight of the decisions, over a point. */
function BeamGlyph({ className = "", tilt = -10, size = 44, ink = "var(--ink)" }: { className?: string; tilt?: number; size?: number; ink?: string }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 56 56" aria-hidden="true">
      <line x1="8" y1="26" x2="48" y2="26" stroke="var(--for)" strokeWidth="5.5" strokeLinecap="round" transform={`rotate(${tilt} 28 26)`} style={{ "--tilt": `${tilt}deg` } as CSSProperties} />
      <circle cx="28" cy="36" r="5.5" fill={ink} />
    </svg>
  )
}

const ICONS: Record<string, ReactNode> = {
  "01": <path d="M4 6h16M4 11h10M4 16h7" />,
  "02": <><path d="M4 13l16-5" /><circle cx="12" cy="16" r="2" fill="currentColor" stroke="none" /><path d="M12 18v2" /></>,
  "03": <path d="M9 7H6a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v2M19 7h-3a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v2" />,
  "04": <path d="M7 5h10M7 9h10M7 5c5 0 6 1.5 6 3.5S12 12 7 12l7 7" />,
  "05": <><path d="M5 5h14M5 9h9" /><rect x="5" y="14" width="14" height="5" rx="1.5" /></>,
  "06": <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>,
}

const RULES: { n: string; t: string; note: string; long: string }[] = [
  { n: "01", t: "Verdict first", long: "Verdict first",
    note: "One sentence you can act on, then the reasons. Never a summary with the conclusion buried at the end." },
  { n: "02", t: "Both sides, weighed", long: "Both sides, weighed for where you are",
    note: "Every decision marked helps or hurts, ranked by how strongly it binds in your High Court. The beam tilts; it never pretends to be level." },
  { n: "03", t: "The court's own words", long: "The court's own words, one tap away",
    note: "Every claim links to a paragraph, a notification, a circular. We quote; we do not paraphrase and call it law." },
  { n: "04", t: "Rupees, never odds", long: "Rupees, never odds",
    note: "What each path costs today and how fast it grows. Never your chances of winning; nobody knows them, and a number would be believed." },
  { n: "05", t: "Limits, stated once", long: "Limits, stated once, in the right place",
    note: "What Nira cannot see sits at the end, in plain words. Not four warnings scattered through the page." },
  { n: "06", t: "Your documents are yours", long: "Your documents are yours",
    note: "Read for the answer you asked for, and nothing else. Never used to train a model. Gone from your account when you delete it, and erased from our systems within 30 days." },
]
const rule = (n: string) => RULES.find((r) => r.n === n) as (typeof RULES)[number]
// Which side of the card each rule's note sits on, top to bottom (handoff 5 part 2 §2).
const LEFT = ["01", "02", "05"]
const RIGHT = ["04", "03", "06"]

function Lean({ lean }: { lean: "helps" | "hurts" }) {
  return (
    <svg className={`nhw-lean ${lean}`} viewBox="0 0 56 56" aria-hidden="true">
      <line x1="8" y1="26" x2="48" y2="26" strokeWidth="7" strokeLinecap="round" />
      <circle cx="28" cy="36" r="7" />
    </svg>
  )
}

/** One answer, dissected: the card, a marker on its edge at each part a rule governs, and the rules as
 * margin notes pinned to their marker's height (measured, not placed by pixel). */
function Dissected() {
  const grid = useRef<HTMLDivElement>(null)
  const marks = useRef<Record<string, HTMLDivElement | null>>({})
  const notes = useRef<Record<string, HTMLDivElement | null>>({})
  const cols = useRef<Record<"l" | "r", HTMLDivElement | null>>({ l: null, r: null })

  const place = useCallback(() => {
    const g = grid.current
    if (!g) return
    const wide = window.matchMedia?.("(min-width: 901px)").matches ?? true
    for (const side of ["l", "r"] as const) {
      const col = cols.current[side]
      if (!col) continue
      let floor = 0
      for (const n of side === "l" ? LEFT : RIGHT) {
        const note = notes.current[n]
        const mark = marks.current[n]
        if (!note || !mark) continue
        if (!wide) { note.style.top = ""; continue }
        const want = mark.getBoundingClientRect().top - col.getBoundingClientRect().top
        const top = Math.max(want, floor)
        note.style.top = `${Math.round(top)}px`
        floor = top + note.offsetHeight + 16
      }
      col.style.minHeight = wide ? `${Math.round(floor)}px` : ""
    }
  }, [])

  useLayoutEffect(place, [place])
  useEffect(() => {
    const g = grid.current
    if (!g || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(() => place())
    ro.observe(g)
    document.fonts?.ready.then(place).catch(() => undefined)
    return () => ro.disconnect()
  }, [place])

  const mark = (n: string, right = false) => ({
    ref: (el: HTMLDivElement | null) => { marks.current[n] = el },
    className: `nhw-mark${right ? " right" : ""}`,
    "data-n": n,
  })
  const noteFor = (n: string) => (
    <div key={n} className="nhw-note" ref={(el) => { notes.current[n] = el }}>
      <b>{rule(n).long}</b>
      <span>{rule(n).note}</span>
    </div>
  )

  return (
    <div className="nhw-dissect" data-rv="dissect" ref={grid}>
      <div className="nhw-notes l" ref={(el) => { cols.current.l = el }}>{LEFT.map(noteFor)}</div>
      <div className="nhw-ans" aria-label="An answer, with the six rules marked on it">
        <div className="nhw-bar">
          <BeamGlyph size={16} />
          <span>nira</span>
          <span className="u">Telangana · for a taxpayer · FY 2023-24 · s.74</span>
        </div>
        <div className="nhw-body">
          <div {...mark("01")}>
            <div className="nhw-ey">Where you stand</div>
            <p className="nhw-v">Your credit can be defended. Prove the goods reached you.</p>
          </div>
          <p className="nhw-r">
            The notice says your supplier never filed GSTR-1, so your credit falls under s.16(2)(c). The Supreme Court's reading of that section binds you; two High Courts since have let the credit stand where the goods demonstrably moved. The reply opens on the movement of goods, not on the supplier's default.
          </p>
          <div {...mark("04", true)}>
            <div className="nhw-gl">
              <div><span className="kk">Leans</span><span className="vv nhw-leans"><Lean lean="helps" />For you</span></div>
              <div><span className="kk">Decisions</span><span className="vv">2 help · 1 binds</span></div>
              <div><span className="kk">At stake</span><span className="vv nst-mono">nil to ₹24.0L</span></div>
              <div><span className="kk">Reply by</span><span className="vv nst-mono">14 Nov 2026</span></div>
            </div>
          </div>
          <div {...mark("02")}>
            <div className="nhw-ey">Decisions, weighed for Telangana</div>
            <div className="nhw-dec"><span><Lean lean="helps" /><b>Sunvale Power</b><em>· Calcutta HC · SLP dismissed</em><sup className="nhw-cite">2</sup></span><span className="ng-chip ok">persuasive here</span></div>
            <div className="nhw-dec"><span><Lean lean="helps" /><b>Kaveri Bhavani Traders</b><em>· Madras HC</em><sup className="nhw-cite">3</sup></span><span className="ng-chip ok">persuasive here</span></div>
            <div className="nhw-dec"><span><Lean lean="hurts" /><b>Harborline Coffee</b><em>· Supreme Court</em><sup className="nhw-cite">1</sup></span><span className="ng-chip warn">binds</span></div>
          </div>
          <div {...mark("03", true)}>
            <p className="nhw-r nhw-quote">
              <i>"Invoices and bank payment alone may not be enough. The dealer has to prove the actual movement of goods."</i>
              <sup className="nhw-cite">1</sup> <span>· Harborline Coffee, para 11</span>
            </p>
          </div>
          <div {...mark("05")}>
            <div className="nhw-lim"><b>What Nira cannot see</b>Whether the goods moved. Whether the credit was utilised. Telangana High Court is complete to June 2025. Research, not legal advice.</div>
          </div>
          <div {...mark("06", true)}>
            <div className="nhw-docs">
              <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS["06"]}</svg>
              <span>Read from your notice for this answer and nothing else · never used to train a model · <Link to={PRIVACY_URL}>delete the workspace</Link></span>
            </div>
          </div>
        </div>
      </div>
      <div className="nhw-notes r" ref={(el) => { cols.current.r = el }}>{RIGHT.map(noteFor)}</div>
      <p className="nhw-made">The names in this example are made up.</p>
    </div>
  )
}

// ---- the six demos: each changes on press, never on scroll ----

const VERDICT = [
  "Your notice looks out of time. Raise it first.",
  "It may be arguable, subject to the facts and applicable precedents, that limitation could potentially be raised as a ground in respect of the notice.",
]

function DemoVerdict() {
  const [hedged, setHedged] = useState(false)
  return (
    <div className="nhw-demo">
      <div className="nhw-q swap" key={hedged ? "h" : "v"}>{VERDICT[hedged ? 1 : 0]}</div>
      <button type="button" className={`nhw-sbtn${hedged ? " on" : ""}`} onClick={() => setHedged((v) => !v)}>
        {hedged ? "Show the verdict" : "Show the hedged version"}
      </button>
    </div>
  )
}

function DemoWeight() {
  const [w, setW] = useState(3)
  const label = w >= 2 ? "Leans for you" : w === 1 ? "Mixed" : "Leans against you"
  const tilt = w >= 2 ? -14 : w === 1 ? 0 : 14
  return (
    <div className="nhw-demo">
      <div className="nhw-weigh">
        <svg className="nhw-wbeam" viewBox="0 0 56 56" width="40" height="40" aria-hidden="true">
          <line x1="8" y1="26" x2="48" y2="26" strokeWidth="6" strokeLinecap="round" style={{ transform: `rotate(${tilt}deg)` }} className={tilt > 0 ? "hurts" : tilt < 0 ? "helps" : "mixed"} />
          <circle cx="28" cy="36" r="6" />
        </svg>
        <div><b>{label}</b><div className="nhw-sub">{w} help you · 1 binds against</div></div>
      </div>
      <div className="nhw-slider" aria-hidden="true"><i style={{ left: `${[2, 32, 62, 92][w]}%` }} /></div>
      <div className="nhw-row" role="group" aria-label="Decisions that help">
        {[0, 1, 2, 3].map((n) => (
          <button key={n} type="button" className={`nhw-sbtn${n === w ? " on" : ""}`} aria-pressed={n === w} onClick={() => setW(n)}>
            {n === 0 ? "0 help" : n === 3 ? "3 help" : n}
          </button>
        ))}
      </div>
    </div>
  )
}

function DemoQuote() {
  const [open, setOpen] = useState(false)
  return (
    <div className="nhw-demo">
      <div className="nhw-q">Invoices and bank payment alone may not be enough.<sup className="nhw-cite">1</sup></div>
      <div className={`nhw-para${open ? " open" : ""}`} aria-hidden={!open}>
        <blockquote>
          <i>"…the dealer has to prove the actual movement of goods and the genuineness of the transaction by furnishing the details of the vehicle, payment of freight…"</i>
          <span>Harborline Coffee, SC 2023, para 11 · an example; the name is made up</span>
        </blockquote>
      </div>
      <button type="button" className={`nhw-sbtn${open ? " on" : ""}`} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? "Close" : "Open the paragraph"}
      </button>
    </div>
  )
}

// ₹12 lakh tax, 18% a year interest, 10% penalty: today, a year on, two years on.
const RUPEES = [2400000, 2620000, 2830000]
const RUPEES_LINE = ["in today, if every head holds", "about ₹18,000 more every month", "interest alone passes ₹4 lakh"]
const lakh = (v: number) => `₹${(v / 100000).toFixed(1)}L`

function DemoRupees() {
  const [t, setT] = useState(0)
  const [shown, setShown] = useState(RUPEES[0])
  const from = useRef(RUPEES[0])
  useEffect(() => {
    const to = RUPEES[t]
    if (prefersReducedMotion()) { setShown(to); from.current = to; return }
    let raf = 0
    const start = performance.now()
    const a = from.current
    const step = (now: number) => {
      const p = 1 - Math.pow(1 - Math.min(1, (now - start) / 640), 3)
      const v = Math.round(a + (to - a) * p)
      setShown(v)
      if (p < 1) raf = requestAnimationFrame(step)
      else from.current = to
    }
    raf = requestAnimationFrame(step)
    return () => { cancelAnimationFrame(raf); from.current = to }
  }, [t])
  return (
    <div className="nhw-demo">
      <div className="nhw-big" aria-live="polite">{lakh(shown)}</div>
      <div className="nhw-sub">{RUPEES_LINE[t]}</div>
      <div className="nhw-row" role="group" aria-label="When">
        {["Today", "1 year", "2 years"].map((l, i) => (
          <button key={l} type="button" className={`nhw-sbtn${i === t ? " on" : ""}`} aria-pressed={i === t} onClick={() => setT(i)}>{l}</button>
        ))}
      </div>
    </div>
  )
}

function DemoLimits() {
  return (
    <div className="nhw-demo">
      <div className="nhw-ey">What Nira cannot see</div>
      <div className="nhw-serif">Whether the goods moved. Whether the credit was utilised. Telangana High Court is complete to June 2025.</div>
    </div>
  )
}

function DemoDocuments() {
  return (
    <div className="nhw-demo nhw-docs3">
      <div><b>Read only for your answer.</b> <span>Nothing else.</span></div>
      <div><b>Never used to train a model.</b> <span>There is no setting that turns this on.</span></div>
      <div><b>Yours to delete.</b> <span>Delete the workspace and it goes from your account at once. Copies are erased from our systems within 30 days.</span></div>
      <Link to={PRIVACY_URL}>What Nira does with your documents →</Link>
    </div>
  )
}

const DEMOS: { n: string; t: string; p: string; demo: ReactNode }[] = [
  { n: "01", t: "Verdict first", p: "The same answer two ways. One of them you can act on.", demo: <DemoVerdict /> },
  { n: "02", t: "Both sides, weighed", p: "Move the weight. The beam tilts with the decisions, not with hope.", demo: <DemoWeight /> },
  { n: "03", t: "The court's own words", p: "A claim, and what is behind it. Open the paragraph.", demo: <DemoQuote /> },
  { n: "04", t: "Rupees, never odds", p: "Contest and lose, over time. ₹12 lakh tax, 18% interest, 10% penalty.", demo: <DemoRupees /> },
  { n: "05", t: "Limits, stated once", p: "The last block of every answer. Not a disclaimer in place of one.", demo: <DemoLimits /> },
  { n: "06", t: "Your documents are yours", p: "What happens to a notice you drop, in three lines.", demo: <DemoDocuments /> },
]

/** Nira's How we work (Site-Ethos v2). */
export default function SiteEthos() {
  return (
    <SiteLayout title="How we work">
      <section className="nst-hero nhw-hero" data-stage="hero">
        <div className="nst-wrap nhw-hero-in">
          <BeamGlyph className="nhw-beam" />
          <h1 className="nhw-h1">
            <span className="w">A calm</span> <span className="w">senior partner,</span> <span className="w">who shows</span> <span className="w">their working.</span>
          </h1>
          <p className="nst-lede nhw-sub-lede">
            Six rules we hold ourselves to on every answer. Below, one answer with the rules marked on it; then each rule, for you to try. If we break one, tell us; corrections are credited by name.
          </p>
          <nav className="nhw-idx" aria-label="The six rules">
            {RULES.map((r) => (
              <a key={r.n} href={`#r${r.n}`}>
                <span className="ic"><svg viewBox="0 0 24 24" aria-hidden="true">{ICONS[r.n]}</svg></span>
                <span className="n">{r.n}</span>
                <b>{r.t}</b>
              </a>
            ))}
          </nav>
        </div>
      </section>

      <section className="nst-sec nhw-sec">
        <div className="nst-wrap nst-stack">
          <div className="nst-head-block" data-rv="up">
            <p className="nst-k">One answer, dissected</p>
            <h2 className="nst-h2 narrow">The rules are not a policy page. They are the shape of every answer.</h2>
          </div>
          <Dissected />
        </div>
      </section>

      <section className="nst-sec nst-tint nhw-sec">
        <div className="nst-wrap nst-stack">
          <div className="nst-head-block" data-rv="up">
            <p className="nst-k">Each rule, tried</p>
            <h2 className="nst-h2">Press each one. This is what it changes.</h2>
          </div>
          <div className="nhw-lab" data-rv="stagger">
            {DEMOS.map((d) => (
              <div className="nhw-try" id={`r${d.n}`} key={d.n}>
                <div className="h"><span className="n">{d.n}</span><h2>{d.t}</h2></div>
                <p>{d.p}</p>
                {d.demo}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nst-sec nhw-sec">
        <div className="nst-wrap nhw-sides">
          <div className="nhw-who" data-rv="up">
            <p className="nst-k">Who we are</p>
            <h2 className="nst-h2">Built to read a notice the way it will be decided.</h2>
            <p className="nst-p">Nira is built by Praxeum Intelligence in Hyderabad. We spent a long time learning how orders are actually reasoned: which point decides a head, what the record has to prove, where the time limit falls. Nira reads your notice with that discipline, so what it tells you is what the order is likely to say, not what you hope it will.</p>
            <p className="nhw-serif-line">The name is temporary. The beam is not.</p>
          </div>
          <div className="nhw-three" data-rv="three">
            <div className="nhw-ey">Three questions every order answers</div>
            <div className="q3"><span className="n">1</span><div><b>Is it in time?</b><span>Year by year, under the section that applies.</span></div></div>
            <div className="q3"><span className="n">2</span><div><b>Is intent made out?</b><span>Or does the notice fall from s.74 to s.73.</span></div></div>
            <div className="q3"><span className="n">3</span><div><b>What does the record prove?</b><span>Not what was alleged; what the documents show.</span></div></div>
            <div className="nhw-asks"><BeamGlyph size={22} /> Nira asks them in that order, for every allegation.</div>
          </div>
        </div>
      </section>

      <section className="nst-navy">
        <div className="nst-wrap nst-cta-row">
          <h2 className="nst-h2">See it on a real question.</h2>
          <StartButtons dark />
        </div>
      </section>
    </SiteLayout>
  )
}
